import React, { useMemo } from 'react';
import { Collapse } from '@mui/material';
import { TimeSignal } from 'graphql/types';
import { useTrackRenderCounts } from 'modules/common/components/Render/hooks';
import { useTimeSignalActions } from '../notesSelector/hooks';
import {
  NOTES_GROUP_BY_TYPE,
  useNotesFilterContext
} from '../NotesFilterContextProvider';
import { useTimeSignalExcludeAction } from '../notesSelector/hooks/useTimeSignalExcludeAction';
import NotesListDayGroupHeader from './NotesListDayGroupHeader';
import { NoteListItem } from './NoteListItem';
import { useNotesGroupBy, useGroupDurations } from './groupBy/hooks';
import { useLabelGroups } from './groupBy/hooks';
import { useDayDuration } from './hooks/useDayDuration';
import { DEFAULT_GROUP_KEY } from './groupBy/hooks/useGroupBy/NoOpGroupByStrategy';

interface NotesListDayGroupProps {
  day: string;
  notes: TimeSignal[];
  totalCount: number;
  onExpandCollapse: (day: string) => void;
  groupedNotesExpansionState: Record<string, boolean>;
  expandAll: boolean;
}

const NotesListDayGroup: React.FC<NotesListDayGroupProps> = ({
  day,
  notes,
  totalCount,
  onExpandCollapse,
  groupedNotesExpansionState,
  expandAll
}: NotesListDayGroupProps) => {
  const { handleTimeSignalAction, isActionInProgress } = useTimeSignalActions();
  const {
    handleTimeSignalExcludeAction,
    isActionInProgress: isExcludeActionInProgress
  } = useTimeSignalExcludeAction();
  useTrackRenderCounts('notes-list-day-group', day);

  const {
    filterContext: { groupBy }
  } = useNotesFilterContext();

  const timeoutProp = useMemo(() => {
    return { enter: 250, exit: 100 };
  }, []);

  const isDayGroupExpanded =
    groupedNotesExpansionState[day] === undefined
      ? expandAll
      : groupedNotesExpansionState[day];

  const groupedNotes = useNotesGroupBy(notes, day);
  const groupDurations = useGroupDurations(groupedNotes);
  const dayDuration = useDayDuration(groupDurations);
  const labelGroups = useLabelGroups({
    groupedNotes,
    groupDurations,
    groupedNotesExpansionState,
    expandAll,
    onExpandCollapse,
    day
  });
  return (
    <div data-testid={`notes-list-day-group-${day}`}>
      <NotesListDayGroupHeader
        day={day}
        expanded={isDayGroupExpanded}
        onExpandCollapse={onExpandCollapse}
        notes={notes}
        totalCount={totalCount}
        totalDuration={dayDuration}
      />
      <Collapse
        in={isDayGroupExpanded}
        timeout={timeoutProp}
        unmountOnExit={false}
        mountOnEnter={true}
      >
        {groupBy === NOTES_GROUP_BY_TYPE.LABELS ? (
          <>{labelGroups}</>
        ) : (
          groupedNotes[DEFAULT_GROUP_KEY].map((note: TimeSignal) => (
            <NoteListItem
              key={note.id}
              note={note}
              handleTimeSignalAction={handleTimeSignalAction}
              isActionInProgress={isActionInProgress}
              handleTimeSignalExcludeAction={handleTimeSignalExcludeAction}
              isExcludeActionInProgress={isExcludeActionInProgress}
            />
          ))
        )}
      </Collapse>
    </div>
  );
};

export default React.memo(NotesListDayGroup);
